import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, User as UserIcon, BarChart3 } from "lucide-react";

import { reportApi } from "@/lib/extensions-api";
import { formatCurrency } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import type { ReportPeriod } from "@/types/report";

export function UserPerformancePage() {
  const [userId, setUserId] = useState("");
  const [period, setPeriod] = useState<ReportPeriod>("monthly");
  const [searched, setSearched] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["admin-user-performance", searched, period],
    queryFn: () => reportApi.userPerformance({ user_id: searched!, period }),
    enabled: !!searched,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">User Performance</h1>
        <p className="text-sm text-surface-500 mt-1">
          Investigate a specific customer's sales activity.
        </p>
      </header>

      <Card>
        <CardBody className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-64">
            <Input label="User ID" placeholder="USR000123"
                    value={userId} onChange={(e) => setUserId(e.target.value.toUpperCase())}
                    leftIcon={<UserIcon className="h-4 w-4" />} />
          </div>
          <div className="w-40">
            <Select label="Period" value={period}
                     onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
                     options={[
                       { value: "today",   label: "Today" },
                       { value: "weekly",  label: "This Week" },
                       { value: "monthly", label: "This Month" },
                       { value: "yearly",  label: "This Year" },
                     ]} />
          </div>
          <Button leftIcon={<Search className="h-4 w-4" />}
                   onClick={() => setSearched(userId.trim())}>Search</Button>
        </CardBody>
      </Card>

      {searched && data && (
        !data.found ? (
          <EmptyState icon={UserIcon} title="User not found"
                        description={`No user with ID "${searched}".`} />
        ) : (
          <>
            <Card>
              <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
              <CardBody className="grid gap-4 sm:grid-cols-3">
                <Field label="User ID"   value={<CopyableId value={data.user_id} />} />
                <Field label="Full Name" value={data.full_name ?? "—"} />
                <Field label="Period"    value={String(data.period)} />
                <Field label="Total Sales"
                        value={<span className="text-lg font-bold text-brand-700">
                          {formatCurrency(data.summary?.total_sales ?? "0")}
                        </span>} />
                <Field label="Transactions"
                        value={String(data.summary?.transaction_count ?? 0)} />
              </CardBody>
            </Card>

            <Card>
              <CardHeader><CardTitle>Daily Activity</CardTitle></CardHeader>
              <CardBody className="p-0">
                {(data.breakdown ?? []).length === 0 ? (
                  <EmptyState icon={BarChart3} title="No activity in this period" />
                ) : (
                  <Table>
                    <THead>
                      <TR>
                        <TH>Date</TH>
                        <TH className="text-right">Transactions</TH>
                        <TH className="text-right">Sales</TH>
                      </TR>
                    </THead>
                    <tbody>
                      {data.breakdown!.map((r) => (
                        <TR key={r.date}>
                          <TD>{r.date}</TD>
                          <TD className="text-right">{r.transaction_count}</TD>
                          <TD className="text-right font-semibold">
                            {formatCurrency(r.total_sales)}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </>
        )
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-2xs font-semibold uppercase text-surface-500">{label}</div>
      <div className="mt-1 text-sm text-surface-900">{value}</div>
    </div>
  );
}
